import { apiService } from "../../common/api/apiService";
import { API_ENDPOINTS } from "../../common/api/apiEndpoints";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { LOADING_STATUS } from "../../common/constants";
import { RootState } from "../../app/store";
import {
  InitialPaginationResponse,
  PaginationParameters,
  PaginationResponse,
} from "../../types/pagination.types";

export interface FilterCriteria {
  showDeleted: boolean;
  searchString: string;
  accommodationNumber: number;
  novelty: boolean;
  showInWebShop: boolean;
  showInOwnerPage: boolean;
  accommodationGroupId: string;
  accountId: string;
}

export type AccommodationListItem = {
  id: string;
  accommodationNumber?: string;
  name: string;
  municipality?: string;
  place?: string;
  skiResort?: string;
  region?: string;
  imageUrl?: string;
  imageThumbUrl?: string;
  imageCarouselUrl?: string;
  beds: number;
  extraBeds: number;
  novelty: number;
  showInWebShop: number;
};
export type AvailableAccommodationListItem = {
  accommodationId: string;
  accommodationNumber?: string;
  name: string;
  municipality?: string;
  place?: string;
  skiResort?: string;
  region?: string;
  imageUrl?: string;
  thumbImageUrl?: string;
  beds: number;
  extraBeds: number;
  price: number;
  sportumId?: string;
  interHomeService?: boolean;
  capacityService?: boolean;
};

export interface AccommodationsState {
  accommodationItems: Array<AccommodationListItem>;
  availableAccommodationItems: Array<AvailableAccommodationListItem>;
  availableAccommodationItemsHomePage: Array<AvailableAccommodationListItem>;
  accommodationItemsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  availableAccommodationItemsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  availableAccommodationItemsStatusHomePage:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  isBookingChangePossibleStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  feedbackPagination: PaginationResponse;
  bookingChangePossible: boolean;
}
const initialState: AccommodationsState = {
  accommodationItems: [],
  availableAccommodationItems: [],
  availableAccommodationItemsHomePage: [],
  accommodationItemsStatus: LOADING_STATUS.IDLE,
  availableAccommodationItemsStatus: LOADING_STATUS.IDLE,
  availableAccommodationItemsStatusHomePage: LOADING_STATUS.IDLE,
  isBookingChangePossibleStatus: LOADING_STATUS.IDLE,
  feedbackPagination: InitialPaginationResponse,
  bookingChangePossible: false,
};
export interface AccommodationQueryFilters extends PaginationParameters {
  filterCriteria: FilterCriteria;
}
export interface AvailableAccommodationsFilter {
  startDate: string;
  endDate: string;
  accommodationId?: string;
}

export interface BookingChangePossibleRequest {
  accommodationId: string;
  startDate: string;
  endDate: string;
  existingBookingId: string;
}

export const getAccommodationsAsync = createAsyncThunk(
  "accommodations/getAccommodations",
  async (
    accommodationsFilter: AccommodationQueryFilters,
    { rejectWithValue },
  ) => {
    const accommodationsInfo = await apiService.post(
      API_ENDPOINTS.ACCOMMODATIONS.GET_ACCOMMODATIONS,
      accommodationsFilter,
    );
    return { accommodationsInfo };
  },
);

export const getAvailableAccommodationsAsync = createAsyncThunk(
  "accommodations/getAvailableAccommodations",
  async (
    availableAccommodationsFilter: AvailableAccommodationsFilter,
    { rejectWithValue },
  ) => {
    const availableAccommodationsInfo = await apiService.post(
      API_ENDPOINTS.ACCOMMODATIONS.GET_AVAILABLE_ACCOMMODATIONS,
      availableAccommodationsFilter,
    );
    return { availableAccommodationsInfo };
  },
);

export const getAvailableAccommodationsHomePageAsync = createAsyncThunk(
  "accommodations/getAvailableAccommodationsHomePage",
  async (
    availableAccommodationsFilter: AvailableAccommodationsFilter,
    { rejectWithValue },
  ) => {
    const availableAccommodationsInfo = await apiService.post(
      API_ENDPOINTS.ACCOMMODATIONS.GET_AVAILABLE_ACCOMMODATIONS,
      availableAccommodationsFilter,
    );
    return { availableAccommodationsInfo };
  },
);

export const getIsBookingChangePossible = createAsyncThunk(
  "accommodations/getIsBookingChangePossible",
  async (
    bookingChangePossibleRequest: BookingChangePossibleRequest,
    { rejectWithValue },
  ) => {
    const bookingChangePossible = await apiService.post(
      API_ENDPOINTS.ACCOMMODATIONS.GET_BOOKING_CHANGE_POSSIBLE,
      bookingChangePossibleRequest,
    );
    return { bookingChangePossible };
  },
);

const accommodationsSlice = createSlice({
  name: "accommodations",
  initialState,
  reducers: {
    clearAvailableAccommodationItemsHomePage: (state) => {
      state.availableAccommodationItemsHomePage = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAccommodationsAsync.pending, (state) => {
        state.accommodationItemsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAccommodationsAsync.fulfilled, (state, action) => {
        state.accommodationItemsStatus = LOADING_STATUS.IDLE;
        state.accommodationItems = action.payload.accommodationsInfo.data;
      })
      .addCase(getAccommodationsAsync.rejected, (state) => {
        state.accommodationItemsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAvailableAccommodationsAsync.pending, (state) => {
        state.availableAccommodationItemsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAvailableAccommodationsAsync.fulfilled, (state, action) => {
        state.availableAccommodationItemsStatus = LOADING_STATUS.IDLE;
        state.availableAccommodationItems =
          action.payload.availableAccommodationsInfo.result.accommodations;
      })
      .addCase(getAvailableAccommodationsAsync.rejected, (state) => {
        state.availableAccommodationItemsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAvailableAccommodationsHomePageAsync.pending, (state) => {
        state.availableAccommodationItemsStatusHomePage =
          LOADING_STATUS.LOADING;
      })
      .addCase(
        getAvailableAccommodationsHomePageAsync.fulfilled,
        (state, action) => {
          state.availableAccommodationItemsStatusHomePage = LOADING_STATUS.IDLE;
          state.availableAccommodationItemsHomePage =
            action.payload.availableAccommodationsInfo.result.accommodations;
        },
      )
      .addCase(getAvailableAccommodationsHomePageAsync.rejected, (state) => {
        state.availableAccommodationItemsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getIsBookingChangePossible.pending, (state) => {
        state.isBookingChangePossibleStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getIsBookingChangePossible.fulfilled, (state, action) => {
        state.isBookingChangePossibleStatus = LOADING_STATUS.IDLE;
        console.log(action.payload.bookingChangePossible);
        state.bookingChangePossible = action.payload.bookingChangePossible;
      })
      .addCase(getIsBookingChangePossible.rejected, (state) => {
        state.isBookingChangePossibleStatus = LOADING_STATUS.FAILED;
      });
  },
});

export const selectAccommodationItems = (state: RootState) =>
  state.accommodations.accommodationItems;

export const selectAvailableAccommodationItems = (state: RootState) =>
  state.accommodations.availableAccommodationItems;

export const selectAvailableAccommodationItemsHomePage = (state: RootState) =>
  state.accommodations.availableAccommodationItemsHomePage;

export const { clearAvailableAccommodationItemsHomePage } =
  accommodationsSlice.actions;

export const selectAccommodationsLoadingStatus = (state: RootState) =>
  state.accommodations.accommodationItemsStatus;

export const selectAvailableAccommodationsLoadingStatus = (state: RootState) =>
  state.accommodations.availableAccommodationItemsStatus;

export const selectAvailableAccommodationsLoadingStatusHomePage = (
  state: RootState,
) => state.accommodations.availableAccommodationItemsStatusHomePage;

export const selectIsBookingChangePossibleStatus = (state: RootState) =>
  state.accommodations.isBookingChangePossibleStatus;

export const selectIsBookingChangePossible = (state: RootState) =>
  state.accommodations.bookingChangePossible;

export default accommodationsSlice.reducer;
